import React, { useEffect, useState } from "react";

const GROUPS = [
  ["T","T","T","t","t","t","t"],   // 3 épaisses → 3
  ["T","T","t","t","t","t","t"],   // 2 épaisses → 2
  ["T","T","T","T","t","t"],       // 4 épaisses → 4
  ["T","t","t","t","t","t","t"],   // 1 épaisse  → 1
];

function BarGroup({ bars }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 3 }}>
      {bars.map((type, i) => {
        const thick = type === "T";
        return (
          <div
            key={i}
            style={{
              width: thick ? 8 : 3,
              height: thick ? 52 : 30 + Math.floor(Math.sin(i * 1.7) * 5),
              background: thick
                ? "linear-gradient(to bottom, #00e5ff, #0088cc)"
                : "linear-gradient(to bottom, rgba(0,180,255,0.75), rgba(0,100,180,0.55))",
              borderRadius: thick ? 2 : 1,
              boxShadow: thick
                ? "0 0 8px rgba(0,229,255,0.8), 0 0 20px rgba(0,180,255,0.4)"
                : "0 0 3px rgba(0,180,255,0.2)",
              flexShrink: 0,
            }}
          />
        );
      })}
    </div>
  );
}

export default function BadgeReader({ onClose }) {
  const [scanLine, setScanLine] = useState(0);
  const [glitch, setGlitch] = useState(false);

  useEffect(() => {
    const id = setInterval(() => {
      setScanLine(v => (v + 2) % 100);
    }, 16);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const schedule = () => {
      const delay = 4000 + Math.random() * 3000;
      return setTimeout(() => {
        setGlitch(true);
        setTimeout(() => setGlitch(false), 120);
        schedule();
      }, delay);
    };
    const id = schedule();
    return () => clearTimeout(id);
  }, []);

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "32px 16px",
      background: "radial-gradient(ellipse at 50% 0%, rgba(0,80,120,0.3) 0%, transparent 70%), #050a0f",
    }}>
      <style>{`
        @keyframes badge-flicker {
          0%, 100% { opacity: 1; }
          92% { opacity: 1; }
          93% { opacity: 0.85; }
          94% { opacity: 1; }
          96% { opacity: 0.9; }
          97% { opacity: 1; }
        }
        @keyframes corner-pulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
        @keyframes status-blink {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
      `}</style>

      <div style={{ width: "100%", maxWidth: 560, animation: "badge-flicker 6s ease-in-out infinite" }}>

        {/* Badge principal */}
        <div style={{
          background: "linear-gradient(145deg, #060d14 0%, #091620 50%, #060d14 100%)",
          border: "1px solid rgba(0,180,255,0.3)",
          borderRadius: 12,
          padding: "2px",
          boxShadow: "0 0 40px rgba(0,150,255,0.15), 0 0 80px rgba(0,80,160,0.1), inset 0 0 20px rgba(0,0,0,0.5)",
          position: "relative",
          overflow: "hidden",
        }}>

          {/* Scan line */}
          <div style={{
            position: "absolute",
            left: 0, right: 0,
            height: 2,
            top: `${scanLine}%`,
            background: "linear-gradient(to right, transparent, rgba(0,229,255,0.15), rgba(0,229,255,0.08), transparent)",
            pointerEvents: "none",
            zIndex: 10,
          }} />

          {/* Glitch */}
          {glitch && (
            <div style={{
              position: "absolute", inset: 0,
              background: "rgba(0,229,255,0.04)",
              transform: "translateX(2px)",
              pointerEvents: "none",
              zIndex: 11,
            }} />
          )}

          {/* Corps interne */}
          <div style={{
            background: "linear-gradient(160deg, #070f18 0%, #0a1a28 100%)",
            borderRadius: 11,
            padding: "24px",
            position: "relative",
          }}>

            {/* Coins déco */}
            {[
              { top: 8, left: 8, borderTop: "1.5px solid rgba(0,229,255,0.6)", borderLeft: "1.5px solid rgba(0,229,255,0.6)", delay: "0s" },
              { top: 8, right: 8, borderTop: "1.5px solid rgba(0,229,255,0.6)", borderRight: "1.5px solid rgba(0,229,255,0.6)", delay: "0.5s" },
              { bottom: 8, left: 8, borderBottom: "1.5px solid rgba(0,229,255,0.6)", borderLeft: "1.5px solid rgba(0,229,255,0.6)", delay: "1s" },
              { bottom: 8, right: 8, borderBottom: "1.5px solid rgba(0,229,255,0.6)", borderRight: "1.5px solid rgba(0,229,255,0.6)", delay: "1.5s" },
            ].map((style, i) => (
              <div key={i} style={{
                position: "absolute",
                width: 14, height: 14,
                animation: "corner-pulse 3s ease-in-out infinite",
                animationDelay: style.delay,
                ...style,
                delay: undefined,
              }} />
            ))}

            {/* Header */}
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 16,
              paddingBottom: 12,
              borderBottom: "1px solid rgba(0,180,255,0.15)",
            }}>
              <div>
                <div style={{
                  fontFamily: "ui-monospace,'Courier New',monospace",
                  fontSize: 9, letterSpacing: "0.35em",
                  color: "rgba(0,229,255,0.5)",
                  textTransform: "uppercase", marginBottom: 2,
                }}>◈ ORIGAMI CORP</div>
                <div style={{
                  fontFamily: "ui-monospace,monospace",
                  fontSize: 15, fontWeight: 800, letterSpacing: "0.12em",
                  color: "rgba(255,255,255,0.92)", textTransform: "uppercase",
                }}>Badge Employé</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{
                  fontFamily: "ui-monospace,monospace",
                  fontSize: 8, letterSpacing: "0.2em",
                  color: "rgba(0,229,255,0.4)", marginBottom: 4,
                }}>STATUT SYSTÈME</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
                  <div style={{
                    width: 7, height: 7, borderRadius: "50%",
                    background: "#00ff88", boxShadow: "0 0 8px #00ff88",
                    animation: "status-blink 1.2s step-end infinite",
                  }} />
                  <span style={{
                    fontFamily: "ui-monospace,monospace",
                    fontSize: 9, color: "#00ff88", letterSpacing: "0.15em",
                  }}>ACTIF</span>
                </div>
              </div>
            </div>

            {/* Photo + Info */}
            <div style={{ display: "flex", gap: 20, marginBottom: 20 }}>
              <div style={{
                width: 88, height: 88,
                background: "linear-gradient(145deg, #0d1f2d, #091520)",
                border: "1px solid rgba(0,180,255,0.25)",
                borderRadius: 8,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, position: "relative", overflow: "hidden",
              }}>
                <svg width="38" height="38" viewBox="0 0 20 20" fill="rgba(0,180,255,0.35)">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                <div style={{
                  position: "absolute", inset: 0,
                  backgroundImage: "linear-gradient(rgba(0,180,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(0,180,255,0.06) 1px, transparent 1px)",
                  backgroundSize: "12px 12px",
                }} />
              </div>

              <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: 6 }}>
                <div style={{
                  fontFamily: "ui-monospace,monospace",
                  fontSize: 17, fontWeight: 900, letterSpacing: "0.05em", color: "white",
                }}>Martin SCHMITT</div>
                <div style={{
                  fontFamily: "ui-monospace,monospace",
                  fontSize: 10, color: "rgba(0,229,255,0.7)",
                  letterSpacing: "0.12em", textTransform: "uppercase",
                }}>Tech Lead Frontend</div>
                <div style={{
                  fontFamily: "ui-monospace,monospace",
                  fontSize: 9, color: "rgba(255,255,255,0.35)", letterSpacing: "0.08em",
                }}>Équipe Origami UI</div>
              </div>
            </div>

            {/* Metadata */}
            <div style={{
              display: "grid", gridTemplateColumns: "repeat(3, 1fr)",
              gap: 12, padding: "12px 0",
              borderTop: "1px solid rgba(0,180,255,0.1)",
              borderBottom: "1px solid rgba(0,180,255,0.1)",
              marginBottom: 20,
            }}>
              {[
                { label: "ID Badge", value: "#5194" },
                { label: "Département", value: "UI-07" },
                { label: "Niveau accès", value: "L3" },
              ].map(({ label, value }) => (
                <div key={label}>
                  <div style={{
                    fontFamily: "ui-monospace,monospace",
                    fontSize: 8, color: "rgba(0,180,255,0.4)",
                    letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 4,
                  }}>{label}</div>
                  <div style={{
                    fontFamily: "ui-monospace,monospace",
                    fontSize: 16, fontWeight: 800,
                    color: "rgba(255,255,255,0.88)", letterSpacing: "0.05em",
                  }}>{value}</div>
                </div>
              ))}
            </div>

            {/* Barcode */}
            <div>
              <div style={{
                fontFamily: "ui-monospace,monospace",
                fontSize: 10, color: "rgba(0,229,255,1)",
                letterSpacing: "0.25em", textTransform: "uppercase",
                marginBottom: 10, textAlign: "center",
              }}>◈ Code d'accès session ◈</div>

              <div style={{
                background: "#000",
                borderRadius: 8,
                border: "1px solid rgba(0,229,255,0.2)",
                padding: "16px 20px",
                position: "relative", overflow: "hidden",
                boxShadow: "inset 0 0 30px rgba(0,0,0,0.8)",
              }}>
                {/* Grille cyber */}
                <div style={{
                  position: "absolute", inset: 0,
                  backgroundImage: "linear-gradient(rgba(0,180,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,180,255,0.04) 1px, transparent 1px)",
                  backgroundSize: "16px 16px",
                  pointerEvents: "none",
                }} />

                {/* Ligne de base */}
                <div style={{
                  position: "absolute",
                  bottom: 28, left: 20, right: 20,
                  height: 1,
                  background: "rgba(0,229,255,0.12)",
                }} />

                {/* Groupes de barres */}
                <div style={{
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                  gap: 8,
                  position: "relative",
                  zIndex: 2,
                  paddingBottom: 18,
                }}>
                  {GROUPS.map((bars, i) => (
                    <BarGroup key={i} bars={bars} />
                  ))}
                </div>

                {/* ID bas */}
                <div style={{
                  position: "absolute",
                  bottom: 8, left: 0, right: 0,
                  textAlign: "center",
                  fontFamily: "ui-monospace,monospace",
                  fontSize: 7, letterSpacing: "0.4em",
                  color: "rgba(0,180,255,0.2)",
                }}>
                  NX-5194-UI07-L3-2025
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Bouton fermer */}
        <button
          onClick={onClose}
          style={{
            marginTop: 20, width: "100%", padding: "12px",
            background: "rgba(0,180,255,0.08)",
            border: "1px solid rgba(0,180,255,0.25)",
            borderRadius: 8,
            color: "rgba(0,229,255,0.7)",
            fontFamily: "ui-monospace,monospace",
            fontSize: 11, fontWeight: 700,
            letterSpacing: "0.2em", textTransform: "uppercase",
            cursor: "pointer", transition: "all 0.2s",
            clipPath: "polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(0,180,255,0.16)"; e.currentTarget.style.color = "rgba(0,229,255,1)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(0,180,255,0.08)"; e.currentTarget.style.color = "rgba(0,229,255,0.7)"; }}
        >
          ✕ Fermer
        </button>
      </div>
    </div>
  );
}